import jsPDF from 'jspdf'
import { format } from 'date-fns'

/**
 * Generate a legal proof PDF for an approved project version
 * Contains immutable approval record with timestamps, IP, and metadata
 */
export const generateApprovalProof = async (project, version, approval) => {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  })

  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const margin = 20
  let yPosition = margin

  // Header
  pdf.setFontSize(24)
  pdf.setFont('helvetica', 'bold')
  pdf.text('Approval Proof Certificate', margin, yPosition)
  yPosition += 15

  pdf.setFontSize(10)
  pdf.setFont('helvetica', 'normal')
  pdf.setTextColor(100)
  pdf.text(`Generated: ${format(new Date(), 'PPpp')}`, margin, yPosition)
  yPosition += 15

  // Divider
  pdf.setDrawColor(200)
  pdf.line(margin, yPosition, pageWidth - margin, yPosition)
  yPosition += 10

  // Project Information
  pdf.setFontSize(14)
  pdf.setFont('helvetica', 'bold')
  pdf.setTextColor(0)
  pdf.text('Project Information', margin, yPosition)
  yPosition += 8

  pdf.setFontSize(10)
  pdf.setFont('helvetica', 'normal')
  pdf.text(`Project: ${project.title}`, margin, yPosition)
  yPosition += 6
  
  if (project.description) {
    const descLines = pdf.splitTextToSize(project.description, pageWidth - 2 * margin)
    pdf.text(descLines, margin, yPosition)
    yPosition += 6 * descLines.length
  }

  pdf.text(`Client: ${project.client_name || project.client_email || 'N/A'}`, margin, yPosition)
  yPosition += 6
  pdf.text(`Project ID: ${project.id}`, margin, yPosition)
  yPosition += 10

  // Version Information
  pdf.setFontSize(14)
  pdf.setFont('helvetica', 'bold')
  pdf.text('Version Information', margin, yPosition)
  yPosition += 8

  pdf.setFontSize(10)
  pdf.setFont('helvetica', 'normal')
  pdf.text(`Version Number: ${version.version_number}`, margin, yPosition)
  yPosition += 6
  pdf.text(`File: ${version.file_name}`, margin, yPosition)
  yPosition += 6
  pdf.text(`Uploaded: ${format(new Date(version.created_at), 'PPpp')}`, margin, yPosition)
  yPosition += 6
  pdf.text(`Version ID: ${version.id}`, margin, yPosition)
  yPosition += 10

  // Divider
  pdf.setDrawColor(200)
  pdf.line(margin, yPosition, pageWidth - margin, yPosition)
  yPosition += 10

  // Approval Information
  pdf.setFontSize(16)
  pdf.setFont('helvetica', 'bold')
  pdf.setTextColor(0, 128, 0)
  pdf.text('APPROVAL RECORD', margin, yPosition)
  yPosition += 10

  pdf.setFontSize(10)
  pdf.setFont('helvetica', 'normal')
  pdf.setTextColor(0)
  pdf.text(`Status: ${approval.status.toUpperCase()}`, margin, yPosition)
  yPosition += 6
  pdf.text(`Approved By: ${approval.client_email || approval.user?.email || 'Unknown'}`, margin, yPosition)
  yPosition += 6
  pdf.text(`Approval Date: ${format(new Date(approval.approved_at), 'PPpp')}`, margin, yPosition)
  yPosition += 6
  pdf.text(`IP Address: ${approval.ip_address || 'N/A'}`, margin, yPosition)
  yPosition += 6
  pdf.text(`User Agent: ${approval.user_agent || 'N/A'}`, margin, yPosition)
  yPosition += 6
  pdf.text(`Approval ID: ${approval.id}`, margin, yPosition)
  yPosition += 10

  if (approval.comment) {
    pdf.setFont('helvetica', 'bold')
    pdf.text('Comment:', margin, yPosition)
    yPosition += 6
    pdf.setFont('helvetica', 'normal')
    const commentLines = pdf.splitTextToSize(approval.comment, pageWidth - 2 * margin)
    pdf.text(commentLines, margin, yPosition)
    yPosition += 6 * commentLines.length + 4
  }

  // Divider
  pdf.setDrawColor(200)
  pdf.line(margin, yPosition, pageWidth - margin, yPosition)
  yPosition += 10

  // Digital Signature Section
  pdf.setFontSize(12)
  pdf.setFont('helvetica', 'bold')
  pdf.text('Digital Signature', margin, yPosition)
  yPosition += 8

  pdf.setFontSize(9)
  pdf.setFont('helvetica', 'normal')
  pdf.setTextColor(100)
  
  // Generate a hash representation
  const hashData = `${project.id}${version.id}${approval.id}${approval.approved_at}`
  const hashDisplay = await generateHash(hashData)
  
  pdf.text('Document Hash:', margin, yPosition)
  yPosition += 5
  pdf.setFont('courier', 'normal')
  pdf.setFontSize(8)
  pdf.text(hashDisplay, margin, yPosition)
  yPosition += 10

  // Footer
  yPosition = pageHeight - 30
  pdf.setDrawColor(200)
  pdf.line(margin, yPosition, pageWidth - margin, yPosition)
  yPosition += 6

  pdf.setFontSize(8)
  pdf.setFont('helvetica', 'italic')
  pdf.setTextColor(100)
  pdf.text('This document serves as legal proof of approval for the specified project version.', margin, yPosition)
  yPosition += 4
  pdf.text('This approval record is immutable and cryptographically verifiable.', margin, yPosition)
  yPosition += 4
  pdf.text('ProofFlow - Creative Approval & Client Proofing System', margin, yPosition)

  // Save the PDF
  const fileName = `ProofFlow_Approval_${project.title.replace(/[^a-z0-9]/gi, '_')}_v${version.version_number}.pdf`
  pdf.save(fileName)
  
  return fileName
}

/**
 * Generate a simple hash for display purposes
 * In production, use a proper cryptographic hash
 */
async function generateHash(data) {
  if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
    const encoder = new TextEncoder()
    const dataBuffer = encoder.encode(data)
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', dataBuffer)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
    return hashHex
  } else {
    // Fallback for environments without crypto API
    return btoa(data).substring(0, 64)
  }
}

/**
 * Generate project summary PDF
 */
export const generateProjectSummary = (project, versions) => {
  const pdf = new jsPDF()
  const margin = 20
  let yPosition = margin

  pdf.setFontSize(20)
  pdf.setFont('helvetica', 'bold')
  pdf.text('Project Summary', margin, yPosition)
  yPosition += 15

  pdf.setFontSize(12)
  pdf.setFont('helvetica', 'normal')
  pdf.text(`Project: ${project.title}`, margin, yPosition)
  yPosition += 8
  pdf.text(`Total Versions: ${versions.length}`, margin, yPosition)
  yPosition += 8
  pdf.text(`Status: ${project.status}`, margin, yPosition)

  const fileName = `ProofFlow_Summary_${project.title.replace(/[^a-z0-9]/gi, '_')}.pdf`
  pdf.save(fileName)
  
  return fileName
}
